import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// Auth via HttpOnly `ens_token` cookie (set oleh backend saat login).
// `withCredentials: true` memastikan cookie ikut dikirim di setiap request.
// Bearer header tidak lagi digunakan — token tidak pernah disentuh oleh JS.
const client = axios.create({ baseURL: API, withCredentials: true });

// One-time cleanup: buang legacy `ens_token` dari localStorage kalau masih ada
// (peninggalan dari session sebelum migrasi cookie).
try {
  if (typeof window !== "undefined" && window.localStorage) {
    localStorage.removeItem("ens_token");
  }
} catch { /* ignore */ }

// 149-cd: Limited retry untuk transient errors (5xx & network) pada GET saja.
// Hanya endpoint idempotent boleh di-retry otomatis. Max 2 retry dgn backoff
// 400ms, 900ms. TIDAK apply ke POST/PATCH/DELETE (bisa duplikasi data). 401/403
// tidak di-retry karena bukan transient.
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const MAX_RETRIES = 2;

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const url = cfg.url || "";
    const detail = err.response?.data?.detail;
    const status = err.response?.status;

    // Do NOT auto-logout on 401 from endpoints yang pakai password re-verification
    // (mis. /auth/salary), karena 401 di situ berarti "password salah", bukan session expired.
    const isPasswordCheck = url.includes("/auth/salary");
    // Do NOT auto-logout on 401 dari /auth/logout — endpoint ini boleh 401
    // kalau cookie sudah invalid; itu expected saat proses logout.
    const isLogoutCall = url.includes("/auth/logout");
    // Public auth pages (login, forgot-password, reset-password) DO NOT require
    // an active session — /auth/me returning 401 there is expected. Never
    // hijack navigation on those routes.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isPublicAuthPage = path === "/login" || path === "/forgot-password" || path === "/reset-password";
    if (status === 401 && !isPasswordCheck && !isLogoutCall && !isPublicAuthPage) {
      // Single-session enforcement: kalau session di-invalidate karena login di device lain,
      // simpan flag di sessionStorage supaya LoginPage bisa tampilkan toast peringatan.
      if (detail === "SESSION_INVALIDATED") {
        sessionStorage.setItem("ens_session_kicked", "1");
      }
      try { localStorage.removeItem("ens_user"); } catch { /* ignore */ }
      window.location.href = "/login";
      return new Promise(() => {});
    }

    // 149-cd: Retry transient 5xx / network errors untuk GET request saja.
    // TIDAK auto-logout untuk 5xx — return error dan biarkan komponen render error state.
    const method = (cfg.method || "get").toLowerCase();
    const isNetwork = !err.response; // ECONNRESET, timeout, etc.
    const canRetry = method === "get" && (isNetwork || (status && RETRYABLE_STATUSES.has(status)));
    if (canRetry) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= MAX_RETRIES) {
        const delay = 400 * Math.pow(2, cfg.__retryCount - 1);
        await new Promise((r) => setTimeout(r, delay));
        return client(cfg);
      }
    }
    return Promise.reject(err);
  }
);

export default client;

export function formatDetail(detail) {
  if (detail == null) return "Terjadi kesalahan. Coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
